import React from "react";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  BarChart3,
  Building2,
  CheckCircle2,
  FileSearch,
  Shield,
  Sparkles,
  Waves,
} from "lucide-react";
import Header from "../components/Header";

const HIGHLIGHTS = [
  {
    title: "Phân tích rõ theo từng đoạn",
    description: "Hiển thị phần nội dung cần xem xét trực tiếp trên văn bản để dễ phản biện và trao đổi học thuật.",
    icon: CheckCircle2,
  },
  {
    title: "Kiểm tra AI trực quan",
    description: "Phân tầng mức độ nghi ngờ bằng màu sắc, giúp người đọc hiểu kết quả nhanh hơn thay vì chỉ xem điểm số.",
    icon: Sparkles,
  },
  {
    title: "Phù hợp quy trình nội bộ",
    description: "Có thể triển khai cho khoa, bộ môn hoặc phòng khảo thí như một công cụ hỗ trợ rà soát trước nghiệm thu.",
    icon: Building2,
  },
  {
    title: "Báo cáo gọn để lưu trữ",
    description: "Xuất PDF và xem lại lịch sử báo cáo, thuận tiện cho việc lưu hồ sơ, phản hồi và đối chiếu nội bộ.",
    icon: Shield,
  },
];

const METRICS = [
  { value: "2 chế độ", label: "Đạo văn và AI trong cùng một nền tảng" },
  { value: "Phù hợp nội bộ", label: "Có thể triển khai trong cơ sở đào tạo hoặc đơn vị nghiên cứu" },
  { value: "Báo cáo PDF", label: "Dễ lưu trữ, chia sẻ và giải trình kết quả" },
];

const WORKFLOW = [
  "Tiếp nhận bài viết, báo cáo hoặc tài liệu học thuật từ người học và giảng viên.",
  "Phân tích nội dung theo từng đoạn để phát hiện mức độ tương đồng hoặc dấu hiệu AI.",
  "Lưu và xuất báo cáo để hỗ trợ rà soát nội bộ trong khoa, bộ môn hoặc phòng khảo thí.",
];

const HomePage = () => {
  return (
    <div className="app-shell">
      <Header />

      <main className="page-main">
        <section className="home-hero home-hero-single">
          <div className="home-hero-copy">
            <div className="home-kicker">
              <Waves className="h-4 w-4" />
              Hệ thống kiểm tra nội dung cho môi trường học thuật Việt Nam
            </div>
            <h1 className="home-title">
              Kiểm tra đạo văn và dấu hiệu AI với giao diện trực quan, dễ giải thích và phù hợp triển khai nội bộ.
            </h1>
            <p className="home-subtitle">
              Nền tảng được định hướng cho các cơ sở đào tạo, khoa chuyên môn và đơn vị nghiên cứu muốn rà soát bài viết,
              tiểu luận, báo cáo hoặc hồ sơ học thuật trước khi lưu trữ, nghiệm thu hoặc phản hồi.
            </p>

            <div className="home-actions">
              <Link to="/plagiarism">
                <Button size="lg" className="home-primary-button">
                  Bắt đầu kiểm tra đạo văn
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/ai">
                <Button size="lg" variant="outline" className="home-secondary-button">
                  Mở giao diện kiểm tra AI
                </Button>
              </Link>
            </div>

            <div className="home-metrics">
              {METRICS.map((metric) => (
                <div key={metric.value} className="home-metric-card">
                  <div className="home-metric-value">{metric.value}</div>
                  <div className="home-metric-label">{metric.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="home-feature-section">
          <div className="section-heading">
            <div className="section-eyebrow">Ứng dụng thực tế</div>
            <h2 className="section-title">
              Không chỉ là công cụ kiểm tra, đây còn là một quy trình có thể triển khai nội bộ trong cơ sở đào tạo.
            </h2>
          </div>

          <div className="home-feature-grid">
            {HIGHLIGHTS.map((feature) => {
              const Icon = feature.icon;

              return (
                <Card key={feature.title} className="home-feature-card">
                  <div className="home-feature-icon">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </Card>
              );
            })}
          </div>
        </section>

        <section className="home-workflow-section">
          <div className="section-heading">
            <div className="section-eyebrow">Hướng dẫn sử dụng</div>
            <h2 className="section-title">Một luồng làm việc gọn hơn, đặt xuống dưới để trang chủ thoáng và cân đối hơn</h2>
          </div>

          <Card className="home-workflow-card">
            <div className="home-workflow-top">
              <div>
                <p className="home-showcase-label">Quy trình đề xuất</p>
                <h3 className="home-showcase-title">Từ nhập liệu đến báo cáo nội bộ</h3>
              </div>
              <div className="home-showcase-pill">Academic workflow</div>
            </div>

            <div className="home-workflow-grid">
              <div className="home-workflow-mini-card">
                <div className="home-showcase-panel-icon">
                  <BarChart3 className="h-4 w-4" />
                </div>
                <h4>Phân tích trực quan</h4>
                <p>Nhìn nhanh confidence, vùng highlight và phần cần chú ý mà không phải đọc log kỹ thuật.</p>
              </div>

              <div className="home-workflow-mini-card">
                <div className="home-showcase-panel-icon">
                  <FileSearch className="h-4 w-4" />
                </div>
                <h4>Phù hợp rà soát nội bộ</h4>
                <p>Hữu ích cho bước kiểm tra sơ bộ trước khi chuyển tiếp sang phản biện, nghiệm thu hoặc lưu kho hồ sơ.</p>
              </div>
            </div>

            <div className="home-showcase-timeline">
              {WORKFLOW.map((step, index) => (
                <div key={step} className="home-timeline-step">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <p>{step}</p>
                </div>
              ))}
            </div>
          </Card>
        </section>
      </main>
    </div>
  );
};

export default HomePage;
